import React, { useEffect } from 'react';
import {
  Box,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';


const renderSlot = (slot, isMobile, compactActions = false) => (
  typeof slot === 'function' ? slot({ isMobile, compactActions }) : slot
);

const inactivePanelProps = {
  hidden: true,
  'aria-hidden': 'true',
  inert: '',
};

const SalonThemeStudioLayout = ({
  controlsTitle,
  previewTitle,
  controls,
  preview,
  feedback,
  footer,
  mobileView = 'controls',
  onMobileViewChange = () => {},
  mobilePreviewSummary,
}) => {
  const { t } = useTranslation();
  // 320 px controls + 420 px preview + 16 px gap + 48 px desktop margins.
  const isMobile = useMediaQuery('(max-width: 803.95px)');
  const compactActions = useMediaQuery('(max-width: 1099.95px)');
  const footerReserve = isMobile ? '160px' : '144px';
  const controlsActive = mobileView === 'controls';
  const previewActive = mobileView === 'preview';

  useEffect(() => {
    const root = document.documentElement;
    const property = '--salon-theme-action-footer-height';
    const previousValue = root.style.getPropertyValue(property);
    root.style.setProperty(property, footerReserve);
    return () => {
      if (previousValue) root.style.setProperty(property, previousValue);
      else root.style.removeProperty(property);
    };
  }, [footerReserve]);

  const controlsHeading = (
    <Box
      data-testid="salon-theme-controls-heading"
      style={{ minHeight: '32px' }}
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      {controlsTitle}
    </Box>
  );

  const controlsPanel = (
    <Paper
      data-testid={isMobile
        ? 'salon-theme-mobile-controls-content'
        : 'salon-theme-controls-panel'}
      variant="outlined"
      sx={{
        maxHeight: isMobile ? 'none' : 'calc(100vh - 168px)',
        overflowY: isMobile ? 'visible' : 'auto',
      }}
    >
      {controls}
    </Paper>
  );

  const controlsContent = (
    <>
      {controlsHeading}
      {controlsPanel}
    </>
  );

  const previewContent = (
    <>
      <Box
        data-testid="salon-theme-preview-heading"
        style={{ minHeight: '32px' }}
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        {previewTitle}
      </Box>
      <Box
        data-testid={isMobile
          ? 'salon-theme-mobile-preview-content'
          : 'salon-theme-preview-panel'}
        style={isMobile ? undefined : { position: 'sticky', top: '88px' }}
        sx={{ minWidth: 0 }}
      >
        {renderSlot(preview, isMobile)}
      </Box>
    </>
  );

  return (
    <Box
      data-testid="salon-theme-studio"
      style={{ paddingBottom: footerReserve }}
    >
      {isMobile ? (
        <>
          {controlsHeading}
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={mobileView}
            onChange={(_event, nextView) => nextView && onMobileViewChange(nextView)}
            aria-label={t('salon_theme.preview.mobile_view_label')}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="controls">
              {t('salon_theme.mobile.customize')}
            </ToggleButton>
            <ToggleButton value="preview">
              {t('salon_theme.mobile.preview')}
            </ToggleButton>
          </ToggleButtonGroup>
          <Box
            data-testid="salon-theme-mobile-controls-panel"
            {...(controlsActive ? {} : inactivePanelProps)}
          >
            {controlsActive ? mobilePreviewSummary : null}
            <Stack spacing={1.5} sx={{ minWidth: 0 }}>
              {controlsPanel}
            </Stack>
          </Box>
          <Box
            data-testid="salon-theme-mobile-preview-panel"
            {...(previewActive ? {} : inactivePanelProps)}
          >
            <Stack spacing={1.5} sx={{ minWidth: 0 }}>
              {previewContent}
            </Stack>
          </Box>
        </>
      ) : (
        <Box
          data-testid="salon-theme-workspace"
          data-desktop-columns="36-64"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            '@media (min-width: 804px)': {
              gridTemplateColumns: 'minmax(320px, 36%) minmax(420px, 1fr)',
            },
            columnGap: 2,
            alignItems: 'start',
          }}
        >
          <Stack spacing={1.5} sx={{ minWidth: 0 }}>
            {controlsContent}
          </Stack>
          <Stack spacing={1.5} sx={{ minWidth: 0 }}>
            {previewContent}
          </Stack>
        </Box>
      )}
      <Box data-testid="salon-theme-feedback" sx={{ mt: 2 }}>
        {feedback}
      </Box>
      <Paper
        data-testid="salon-theme-footer"
        variant="outlined"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: '16px',
          right: '16px',
          '@media (min-width: 820px)': {
            left: '24px',
            right: '24px',
          },
          zIndex: 5,
          bgcolor: 'background.paper',
          mt: 2,
          p: isMobile ? 1.5 : 2,
        }}
      >
        {renderSlot(footer, isMobile, compactActions)}
      </Paper>
    </Box>
  );
};

export default SalonThemeStudioLayout;
